import React, { useState, useEffect } from 'react';
import {
    Button, Table, Card, Row, Col, Form, InputGroup,
    Spinner, Modal, Badge, Pagination, Tooltip, OverlayTrigger
} from 'react-bootstrap';
import {
    collection, query, where, orderBy, getDocs, doc,
    deleteDoc, updateDoc, serverTimestamp, getFirestore, addDoc
} from 'firebase/firestore';
import firebaseApp from '../../../firebase/credenciales';
import { FaPlus, FaEdit, FaTrash, FaSearch, FaTimes, FaInfoCircle } from 'react-icons/fa';
import Swal from 'sweetalert2';
import PerfilForm from './PerfilForm';

const PerfilesScreen = ({ usuario }) => {
    // Estados para gestionar la pantalla
    const [perfiles, setPerfiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [currentPerfil, setCurrentPerfil] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(8); // Menos items por página que otros catálogos debido a posible complejidad
    const [submitting, setSubmitting] = useState(false); // NUEVO: Estado para controlar envío en curso

    // Acceder a Firestore
    const db = getFirestore(firebaseApp);

    // Obtener RUC y nombre de la empresa del usuario autenticado
    const ruc = usuario?.ruc || '';
    const nombreEmpresa = usuario?.empresa || 'Empresa';

    // Cargar perfiles al iniciar
    useEffect(() => {
        loadPerfiles();
    }, [ruc]);

    // Función para cargar perfiles de la empresa
    const loadPerfiles = async () => {
        try {
            setLoading(true);
            const perfilesRef = collection(db, 'rs_perfiles');
            const q = query(
                perfilesRef,
                where('ruc', '==', ruc),
                orderBy('nombre')
            );

            const snapshot = await getDocs(q);

            const perfilesList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            setPerfiles(perfilesList);
        } catch (error) {
            console.error("Error cargando perfiles:", error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'No se pudieron cargar los perfiles profesionales. Por favor, inténtelo nuevamente.'
            });
        } finally {
            setLoading(false);
        }
    };

    // Filtrar perfiles según término de búsqueda
    const filteredPerfiles = perfiles.filter(perfil =>
        perfil.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (perfil.descripcion && perfil.descripcion.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (perfil.profesionPrincipal && perfil.profesionPrincipal.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    // Calcular paginación
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentPerfiles = filteredPerfiles.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredPerfiles.length / itemsPerPage);

    // Cambiar de página
    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    // Abrir modal para editar
    const handleEdit = (perfil) => {
        setCurrentPerfil(perfil);
        setShowModal(true);
    };

    // Abrir modal para crear nuevo perfil
    const handleNew = () => {
        setCurrentPerfil(null);
        setShowModal(true);
    };

    // Eliminar un perfil
    const handleDelete = async (perfilId, nombrePerfil) => {
        try {
            const result = await Swal.fire({
                title: '¿Está seguro?',
                text: `Va a eliminar el perfil "${nombrePerfil}". Esta acción no se puede deshacer.`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#dc3545',
                cancelButtonColor: '#6c757d',
                confirmButtonText: 'Sí, eliminar',
                cancelButtonText: 'Cancelar'
            });

            if (result.isConfirmed) {
                await deleteDoc(doc(db, 'rs_perfiles', perfilId));

                Swal.fire({
                    icon: 'success',
                    title: 'Eliminado',
                    text: 'El perfil ha sido eliminado correctamente.'
                });

                // Actualizar lista local sin necesidad de recargar de Firestore
                setPerfiles(prevPerfiles =>
                    prevPerfiles.filter(perfil => perfil.id !== perfilId)
                );
            }
        } catch (error) {
            console.error("Error eliminando perfil:", error);

            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'No se pudo eliminar el perfil. Es posible que esté siendo utilizado por vacantes activas.'
            });
        }
    };

    // Guardar un perfil (nuevo o edición)
    const handleSavePerfil = async (formData) => {
        // MODIFICADO: Evitar doble envío
        if (submitting) return;
        
        try {
            setSubmitting(true); // Iniciar estado de envío
            
            if (currentPerfil) {
                // Actualizar perfil existente
                await updateDoc(doc(db, 'rs_perfiles', currentPerfil.id), {
                    ...formData,
                    fechaActualizacion: serverTimestamp()
                });

                // Actualizar lista local
                setPerfiles(prevPerfiles =>
                    prevPerfiles.map(perfil =>
                        perfil.id === currentPerfil.id
                            ? { ...perfil, ...formData }
                            : perfil
                    )
                );

                Swal.fire({
                    icon: 'success',
                    title: 'Actualizado',
                    text: 'Perfil profesional actualizado correctamente'
                });
            } else {
                // Crear nuevo perfil en Firestore
                const perfilesRef = collection(db, 'rs_perfiles');
                const docRef = await addDoc(perfilesRef, {
                    ...formData,
                    fechaCreacion: serverTimestamp(),
                    usos: 0
                });

                // Agregar el nuevo perfil a la lista local con el ID generado
                const newPerfil = {
                    id: docRef.id,
                    ...formData,
                    usos: 0
                };
                setPerfiles(prevPerfiles => [...prevPerfiles, newPerfil]);

                Swal.fire({
                    icon: 'success',
                    title: 'Creado',
                    text: 'Perfil profesional creado correctamente'
                });
            }

            setShowModal(false);
        } catch (error) {
            console.error("Error guardando perfil:", error);

            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Ocurrió un error al guardar el perfil. Por favor, inténtelo nuevamente.'
            });
        } finally {
            setSubmitting(false); // Finalizar estado de envío
        }
    };

    // Renderizar la etiqueta de nivel de experiencia
    const renderExperienciaBadge = (nivel) => {
        if (typeof nivel === 'number') {
            // Si es un número (años de experiencia)
            if (nivel === 0) return <Badge bg="secondary">Sin experiencia</Badge>;
            if (nivel < 2) return <Badge bg="info">{nivel} año(s)</Badge>;
            if (nivel < 5) return <Badge bg="primary">{nivel} años</Badge>;
            return <Badge bg="success">{nivel} años</Badge>;
        }

        // Para compatibilidad con formato anterior basado en texto
        let variant = 'secondary';
        let texto = 'No definido';

        switch (nivel) {
            case 'junior':
                variant = 'info';
                texto = 'Junior';
                break;
            case 'semi-senior':
                variant = 'primary';
                texto = 'Semi-senior';
                break;
            case 'senior':
                variant = 'success';
                texto = 'Senior';
                break;
            case 'expert':
                variant = 'warning';
                texto = 'Experto';
                break;
            default:
                break;
        }

        return <Badge bg={variant}>{texto}</Badge>;
    };

    return (
        <div className="p-1">
            <Row className="mb-4 align-items-center">
                <Col md={8}>
                    <h4 className="mb-0">Gestión de Perfiles Profesionales</h4>
                    <p className="text-muted mb-0">
                        Administre los perfiles profesionales para los procesos de selección
                    </p>
                </Col>
                <Col md={4} className="text-end">
                    <Button
                        variant="primary"
                        onClick={handleNew}
                        className="d-flex align-items-center ms-auto"
                    >
                        <FaPlus className="me-2" /> Nuevo Perfil
                    </Button>
                </Col>
            </Row>

            <Card className="mb-4">
                <Card.Body>
                    <Row className="mb-3">
                        <Col md={6}>
                            <InputGroup>
                                <InputGroup.Text>
                                    <FaSearch />
                                </InputGroup.Text>
                                <Form.Control
                                    type="text"
                                    placeholder="Buscar por nombre, descripción o profesión..."
                                    value={searchTerm}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        setCurrentPage(1); // Resetear a primera página
                                    }}
                                />
                                {searchTerm && (
                                    <Button
                                        variant="outline-secondary"
                                        onClick={() => setSearchTerm('')}
                                    >
                                        <FaTimes />
                                    </Button>
                                )}
                            </InputGroup>
                        </Col>
                        <Col md={6} className="text-end text-muted">
                            {filteredPerfiles.length} perfiles encontrados
                        </Col>
                    </Row>

                    {loading ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-2">Cargando perfiles profesionales...</p>
                        </div>
                    ) : currentPerfiles.length === 0 ? (
                        <div className="text-center py-4">
                            <p className="mb-0 text-muted">
                                {searchTerm
                                    ? 'No se encontraron perfiles que coincidan con la búsqueda.'
                                    : 'No hay perfiles registrados.'}
                            </p>
                            {searchTerm && (
                                <Button
                                    variant="link"
                                    onClick={() => setSearchTerm('')}
                                >
                                    Limpiar búsqueda
                                </Button>
                            )}
                        </div>
                    ) : (
                        <>
                            <Table responsive hover>
                                <thead className="bg-light">
                                    <tr>
                                        <th>#</th>
                                        <th>Nombre del Perfil</th>
                                        <th>Profesión</th>
                                        <th>Experiencia</th>
                                        <th>Requisitos</th>
                                        <th>Usos</th>
                                        <th>Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentPerfiles.map((perfil, index) => (
                                        <tr key={perfil.id}>
                                            <td>{indexOfFirstItem + index + 1}</td>
                                            <td>
                                                {perfil.nombre}
                                                {perfil.descripcion && (
                                                    <OverlayTrigger
                                                        placement="top"
                                                        overlay={
                                                            <Tooltip id={`tooltip-${perfil.id}`}>
                                                                {perfil.descripcion}
                                                            </Tooltip>
                                                        }
                                                    >
                                                        <span className="ms-2">
                                                            <FaInfoCircle className="text-muted" size={14} />
                                                        </span>
                                                    </OverlayTrigger>
                                                )}
                                            </td>
                                            <td>
                                                {perfil.profesiones && perfil.profesiones.length > 0 ? (
                                                    <span>
                                                        {perfil.profesiones[0].nombre}
                                                        {perfil.profesiones.length > 1 && (
                                                            <OverlayTrigger
                                                                placement="top"
                                                                overlay={
                                                                    <Tooltip id={`profesiones-${perfil.id}`}>
                                                                        Profesiones adicionales:<br />
                                                                        {perfil.profesiones.slice(1).map(p => p.nombre).join(', ')}
                                                                    </Tooltip>
                                                                }
                                                            >
                                                                <Badge className="ms-1" bg="info">+{perfil.profesiones.length - 1}</Badge>
                                                            </OverlayTrigger>
                                                        )}
                                                    </span>
                                                ) : (
                                                    perfil.profesionNombre || <span className="text-muted">—</span>
                                                )}
                                            </td>
                                            <td>
                                                {renderExperienciaBadge(perfil.nivelExperiencia)}
                                            </td>
                                            <td>
                                                <div className="d-flex flex-wrap gap-1">
                                                    {perfil.habilidades && perfil.habilidades.length > 0 ? (
                                                        <Badge bg="secondary">
                                                            {perfil.habilidades.length} habilidades
                                                        </Badge>
                                                    ) : (
                                                        <span className="text-muted">—</span>
                                                    )}

                                                    {perfil.especialidades && perfil.especialidades.length > 0 && (
                                                        <Badge bg="info">
                                                            {perfil.especialidades.length} especialidades
                                                        </Badge>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <Badge bg={perfil.usos > 0 ? "success" : "secondary"}>
                                                    {perfil.usos || 0}
                                                </Badge>
                                            </td>
                                            <td>
                                                <div className="d-flex gap-2">
                                                    <Button
                                                        size="sm"
                                                        variant="outline-primary"
                                                        onClick={() => handleEdit(perfil)}
                                                    >
                                                        <FaEdit />
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="outline-danger"
                                                        onClick={() => handleDelete(perfil.id, perfil.nombre)}
                                                        disabled={perfil.usos > 0}
                                                        title={perfil.usos > 0 ? "No se puede eliminar un perfil en uso" : ""}
                                                    >
                                                        <FaTrash />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>

                            {totalPages > 1 && (
                                <div className="d-flex justify-content-center mt-4">
                                    <Pagination>
                                        <Pagination.First onClick={() => paginate(1)} disabled={currentPage === 1} />
                                        <Pagination.Prev onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1} />

                                        {[...Array(totalPages)].map((_, i) => (
                                            <Pagination.Item
                                                key={i + 1}
                                                active={i + 1 === currentPage}
                                                onClick={() => paginate(i + 1)}
                                            >
                                                {i + 1}
                                            </Pagination.Item>
                                        ))}

                                        <Pagination.Next onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages} />
                                        <Pagination.Last onClick={() => paginate(totalPages)} disabled={currentPage === totalPages} />
                                    </Pagination>
                                </div>
                            )}
                        </>
                    )}
                </Card.Body>
            </Card>

            {/* Modal para crear/editar perfil */}
            <Modal
                show={showModal}
                onHide={() => setShowModal(false)}
                backdrop="static"
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {currentPerfil ? 'Editar Perfil Profesional' : 'Nuevo Perfil Profesional'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <PerfilForm
                        perfil={currentPerfil}
                        onSave={handleSavePerfil}
                        onCancel={() => setShowModal(false)}
                        usuario={usuario}
                        submitting={submitting} // NUEVO: Pasar estado de envío al formulario
                    />
                </Modal.Body>
            </Modal>

            {/* Tarjeta informativa */}
            <Card className="bg-light">
                <Card.Body>
                    <h5>Información</h5>
                    <ul className="mb-0">
                        <li>Los perfiles profesionales definen los requisitos específicos para puestos de trabajo</li>
                        <li>Puede asignar múltiples profesiones a cada perfil, la primera será la principal</li>
                        <li>Los perfiles incluyen niveles de experiencia requeridos para el puesto</li>
                        <li>Los perfiles en uso (con valor mayor a 0) no podrán ser eliminados</li>
                        <li>Estos perfiles estarán disponibles al crear o editar vacantes</li>
                    </ul>
                </Card.Body>
            </Card>
        </div>
    );
};

export default PerfilesScreen;